import find from 'lodash/find';

import {
  Constants,
  OxfordPLNFilter,
  PLNFilter,
  plnPseudoCode,
} from '../common/Constants';
import { convertUrlParamsToObject } from '../common/Utils';
import { Eligibility } from '../store/useVirtualCareStore/virtualCareStore';

export const sleep = (delay: number) => {
  let sleepTimeout: NodeJS.Timeout;
  return new Promise((resolve) => {
    sleepTimeout = setTimeout(resolve, delay);
  }).finally(() => {
    if (sleepTimeout) {
      clearTimeout(sleepTimeout);
    }
  });
};

export const conditionalValueRender = (
  condition,
  renderforDefined,
  renderforUnDefined
) => (condition ? renderforDefined : renderforUnDefined);

export const searchResultBreadcrumbTitle = (providerTypeLocal) => {
  let titleAssigned;
  switch (providerTypeLocal) {
    case Constants.RESULT_SECTION.FACILITY:
      titleAssigned = 'Facility results';
      break;
    case Constants.RESULT_SECTION.PROVIDER_GROUPS:
      titleAssigned = 'Provider group results';
      break;
    default:
      titleAssigned = 'Provider results';
      break;
  }
  return titleAssigned;
};

export type LagoonData = {
  id: string;
  key: string;
  pseudoRollupCode: string;
  dntDescription: string;
  action: string;
  filterName: string;
  linkTarget: string;
  providerType: string;
  categoryChips: string;
  coverageType: string;
  isPseudoRollupSearch: boolean;
  state?: string;
};

export const getValueFromPseudoRollupCodeTable = (
  pseudoRollupCode: string,
  LagoonData: LagoonData[],
  dntDescription: string,
  isOXMember?: boolean,
  oxfordPlnFlag?: boolean
): LagoonData | undefined => {
  const lagoonData = LagoonData.filter(
    (item: any) => item.pseudoRollupCode === pseudoRollupCode
  );

  if (pseudoRollupCode === plnPseudoCode) {
    if (oxfordPlnFlag && isOXMember) {
      return lagoonData.find(
        (item: any) => item.filterName === OxfordPLNFilter
      );
    } else {
      return lagoonData.find((item: any) => item.filterName === PLNFilter);
    }
  }

  if (lagoonData?.length > 1) {
    return lagoonData.find(
      (item: any) => item.dntDescription === dntDescription
    );
  }
  return lagoonData?.[0];
};

type PortalDefaultSearchParams = {
  chipValue?: string;
  coverageType?: string;
};

export const getPortalDefaultSearchParams = (
  portalConfig?
): PortalDefaultSearchParams => {
  if (!portalConfig) {
    return {};
  }

  const defaultPortalSearchParams = portalConfig?.homePage
    ?.replace('/', '')
    .replace('?', '');
  const urlParams = new URLSearchParams(defaultPortalSearchParams);
  return convertUrlParamsToObject(urlParams?.entries());
};

export const getUiMessageFromLagoon = (
  lagoonKey: string,
  lagoonData?: any
): string => {
  if (!lagoonKey?.trim()) return '';

  const result = find(lagoonData, {
    key: lagoonKey,
  });
  return result?.message ?? '';
};

export const getBehavioralFacilityProgramsFromLagoon = (
  groupName: string,
  lagoonData?: any
): any[] => {
  if (!groupName?.trim()) return [];

  const result = (lagoonData ?? []).filter((item) => item.group === groupName);
  return result ?? [];
};

export const getIntersectionFromArrays = (arrays: any[]) => {
  if (!arrays || arrays.length === 0) return [];

  let intersectionResult = arrays[0];
  // Iterate through all arrays
  for (let i = 1; i < arrays.length; i++) {
    // Iterate through the current array
    intersectionResult = intersectionResult.filter((value: any) =>
      // Check if the value is in the next array
      arrays[i].includes(value)
    );
  }

  return intersectionResult || [];
};
const directoryLevelSwitch = {
  M: 'useMedicalNewRollUpCodes',
  B: 'useBHNewRollUpCodes',
  V: 'useVisionNewRollUpCodes',
  D: 'useDentalNewRollUpCodes',
};

export const supportNewCodes = (args) => {
  const { coverageType } = args;
  return args[directoryLevelSwitch[coverageType]];
};

export const isNullOrUndefined = (value: any): boolean =>
  value === null || value === undefined;

export const isValidEnumValue = (enumType, enumValue): boolean =>
  Object.values(enumType).includes(enumValue);

export const isExternalUrl = (url?: string) =>
  url?.includes('https://') || url?.includes('http://');

export const isPolicyIdSuppressedForMarketPlace = (
  suppressObj,
  currentMember
) => suppressObj?.policyIds?.split(',').includes(currentMember?.policyNumber);

export const scrollIntoViewById = (
  id: string,
  options?: ScrollIntoViewOptions,
  offset?: number
) => {
  const element = document.getElementById(id);
  if (element) {
    if (offset !== undefined) {
      element.style.scrollMarginTop = `${offset}px`;
    }
    element.scrollIntoView(options || { behavior: 'smooth' });
  }
};

export const convertDisplayNameToTitleCase = (name: string): string =>
  name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : '';

export const getPlanIndicator = (
  planInfo?: Record<string, any>,
  networkId?: string,
  coverageType?: string
): string[] | '' => {
  if (!planInfo || !networkId || !coverageType) return '';

  const networkIds = networkId.split(',').map((id) => id.trim());
  return Object.values(planInfo)
    .filter((plan) =>
      networkIds.some((id) =>
        plan?.coverageTypeReciprocityIdMap[coverageType]
          ?.split(',')
          .map((value) => value.trim())
          .includes(id)
      )
    )
    .map((plan) => plan.population);
};
export const ternaryExpression = <T>(
  condition: boolean,
  value1: T,
  value2: T
): T => (condition ? value1 : value2);

export const hasNonEmptyEligibilities = (
  planEligibilities: Eligibility[]
): boolean => {
  if (!planEligibilities || planEligibilities.length === 0) {
    return false;
  }

  return true;
};

export const checkAscensionScheduleDataIndicator = (
  practitioner: any,
  coverageType: string | undefined
): boolean =>
  coverageType &&
  ['M', 'B'].includes(coverageType) &&
  !practitioner?.hospitalBasedProviderIndicator &&
  practitioner?.ascensionIndicator &&
  isValid(practitioner?.ascensionSchedulingLink) &&
  !practitioner?.isDocasapScheduled;

function isValid(url: string) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return false;
  }
}

export const isPolicyIdPresentForAOESuppression = (
  coverageType: string,
  row: any,
  memberPolicyNumber: string
): Array<string> | undefined =>
  memberPolicyNumber &&
  coverageType === 'M' &&
  row?.policyIds?.includes(memberPolicyNumber.trim())
    ? row.suppressedValues.split(',')
    : [];
